/**
 * Turning raw values into something a person reads.
 *
 * Kit components take text, not numbers — a card shows "$1.2M" because its
 * caller decided that, not because the card guessed. These are the shared
 * defaults for callers with nothing more specific to say.
 */

/** The most decimals any figure shows before it is noise rather than detail. */
export const MAX_DECIMALS = 2;

/**
 * A number as a person reads it: thousands separated, at most two decimals,
 * and never padded to two.
 *
 * A float carries as many digits as the arithmetic left behind —
 * 0.07139999999999999 is a real value out of a real division — and printing
 * all of them says nothing true that "0.07" does not. Two decimals is the
 * ceiling, not a target: 9.9 stays 9.9 and 14 stays 14, because a decimal
 * that was never measured should not be invented.
 *
 * Numeric strings count as numbers, since some sources send "46950.00".
 */
export function formatNumberValue(value: number | string): string {
  const v = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(v)) return String(value);
  // Pinned to en-US, not the machine's locale: the same figure has to read the
  // same in a screenshot, a shared link and a call, and the reader's browser
  // settings are not a fact about the data. Left to the locale, 333,333.33
  // becomes 3,33,333.33 on a machine set to India.
  return v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: MAX_DECIMALS });
}

/**
 * A single figure, as it should read on a card.
 *
 * Numbers follow `formatNumberValue`; anything else is shown as it came.
 */
export function formatMetricValue(value: unknown): string {
  if (value === null || value === undefined) return 'N/A';

  let v: unknown = value;
  if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) {
    v = Number(v);
  }

  if (typeof v === 'number') return formatNumberValue(v);

  if (typeof v === 'boolean') return v ? 'Yes' : 'No';

  return String(v);
}
